import { cn } from "@/lib/utils";

type Props = {
  symbol: string;      // "BTC" | "ETH" | "SOL" | "USDT" | fallback
  network?: string;    // Optional — renders a small bottom-left chain badge (useful for USDT/multi-chain assets)
  size?: number;       // pixel size (width = height) of the main icon
  className?: string;
};

// Official brand colors + the standard unicode glyph for each asset.
// The glyph renders reliably across platforms (fonts include these codepoints)
// and keeps the component zero-dependency.
const BRAND: Record<string, { bg: string; fg: string; mark: string }> = {
  BTC:  { bg: "#f7931a", fg: "#ffffff", mark: "₿" },
  ETH:  { bg: "#627eea", fg: "#ffffff", mark: "Ξ" },
  SOL:  { bg: "#1a1a1a", fg: "#14f195", mark: "◎" },
  USDT: { bg: "#26a17b", fg: "#ffffff", mark: "₮" },
  BNB:  { bg: "#f0b90b", fg: "#1a1a1a", mark: "B" },
  MATIC:{ bg: "#8247e5", fg: "#ffffff", mark: "M" },
  TRX:  { bg: "#ff060a", fg: "#ffffff", mark: "T" },
};

// Per-network emblems for the corner badge.
const NETWORK_EMBLEM: Record<string, { bg: string; fg: string; mark: string }> = {
  BITCOIN:     { bg: "#f7931a", fg: "#ffffff", mark: "₿" },
  ETH_MAINNET: { bg: "#627eea", fg: "#ffffff", mark: "Ξ" },
  BSC:         { bg: "#f0b90b", fg: "#1a1a1a", mark: "B" },
  SOLANA:      { bg: "#14f195", fg: "#1a1a1a", mark: "S" },
  POLYGON:     { bg: "#8247e5", fg: "#ffffff", mark: "P" },
  TRON:        { bg: "#ff060a", fg: "#ffffff", mark: "T" },
};

// When asset and network are effectively the same thing (native coin on its
// home chain), the emblem is redundant noise — suppress it.
function shouldShowEmblem(symbol: string, network?: string): boolean {
  if (!network) return false;
  const redundant: Record<string, string> = {
    BTC:  "BITCOIN",
    ETH:  "ETH_MAINNET",
    SOL:  "SOLANA",
    BNB:  "BSC",
    MATIC:"POLYGON",
  };
  return redundant[symbol] !== network;
}

export function CryptoIcon({ symbol, network, size = 24, className }: Props) {
  const brand = BRAND[symbol] ?? { bg: "#6b7280", fg: "#ffffff", mark: symbol.slice(0, 1).toUpperCase() };
  const showEmblem = shouldShowEmblem(symbol, network);
  const emblem = showEmblem && network ? NETWORK_EMBLEM[network] : null;

  // Emblem sits in the bottom-left, occupies ~42% of main icon, with a white
  // ring so it doesn't blend into the parent.
  const emblemSize = Math.max(12, Math.round(size * 0.42));
  const emblemOffset = Math.round(emblemSize * 0.18);

  const mainIcon = (
    <span
      role="img"
      aria-label={symbol}
      className="inline-flex items-center justify-center rounded-full font-bold leading-none select-none shadow-sm ring-1 ring-black/5"
      style={{
        width: size,
        height: size,
        background: brand.bg,
        color: brand.fg,
        fontSize: Math.round(size * 0.52),
      }}
    >
      {brand.mark}
    </span>
  );

  if (!emblem) {
    return <span className={cn("inline-flex shrink-0", className)}>{mainIcon}</span>;
  }

  return (
    <span
      className={cn("relative inline-flex shrink-0", className)}
      style={{ width: size, height: size }}
      aria-label={`${symbol} on ${network}`}
    >
      {mainIcon}
      <span
        className="absolute inline-flex items-center justify-center rounded-full font-bold leading-none select-none ring-2 ring-background"
        style={{
          width: emblemSize,
          height: emblemSize,
          left: -emblemOffset,
          bottom: -emblemOffset,
          background: emblem.bg,
          color: emblem.fg,
          fontSize: Math.round(emblemSize * 0.6),
        }}
        aria-hidden
      >
        {emblem.mark}
      </span>
    </span>
  );
}
